<script setup lang="ts">
import AppErrorPageBlock from '~/components/app/AppErrorPageBlock.vue'
import DirectionPageLoadingSkeleton from '~/components/Direction/page/DirectionPageLoadingSkeleton.vue'
import DirectionOrgGridSkeleton from '~/components/Direction/page/DirectionOrgGridSkeleton.vue'
import OrgaBarSkeleton from '~/components/orga/OrgaBarSkeleton.vue'
import { useIsOrganigrammeEditing, useOrganigrammeEditRouteAccess } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import { useOrgEditActor } from '~/composables/auth/useOrgEditActor'
import { useEditScopeToast } from '~/composables/auth/useEditScopeToast'
import { actorMayCreateOrDeleteDirection } from '~~/utils/auth/orgEditAffordances'
import OrgReorderChrome from '~/components/orga/reorder/OrgReorderChrome.vue'
import { useDirectionDomaineReorder } from '~/composables/orga/reorder/useDirectionDomaineReorder'
import { directionPageEditScopeNotice } from '~~/utils/auth/orgEditScopeNotice'
import { useDirectionPageLoad } from '~/composables/direction/page/useDirectionPageLoad'
import { useDirectionPageOverlays } from '~/composables/direction/page/useDirectionPageOverlays'
import { useDirectionPageBreadcrumbPreview } from '~/composables/direction/page/useDirectionPageBreadcrumbPreview'
import { useOrgLocateHighlight } from '~/composables/orga/useOrgLocateHighlight'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const DirectionPageCodirBar = defineAsyncWithSkeleton(
  function loadDirectionPageCodirBar() {
    return import('~/components/Direction/page/DirectionPageCodirBar.vue')
  },
  OrgaBarSkeleton,
)

const DirectionDomainesGrid = defineAsyncWithSkeleton(
  function loadDirectionDomainesGrid() {
    return import('~/components/Direction/page/DirectionDomainesGrid.vue')
  },
  DirectionOrgGridSkeleton,
)

const DirectionPageOverlays = defineAsyncWithSkeleton(
  function loadDirectionPageOverlays() {
    return import('~/components/Direction/page/DirectionPageOverlays.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const route = useRoute()
const { canEditOrganigramOnCurrentView, myManagerOrganigramEditScope } = useOrganigrammeEditRouteAccess()
const isEditing = useIsOrganigrammeEditing()
const { actor } = useOrgEditActor()

const {
  pageState,
  errorScreenModel,
  isDirectionOrganisationEmpty,
  title,
  grid,
  directionLib,
  codir,
  isReadyForHighlight,
  refetch,
} = useDirectionPageLoad()

const domaineReorder = useDirectionDomaineReorder(grid, directionLib, refetch)
const domaineReorderRoot = domaineReorder.rootRef
const domaineReorderVisible = domaineReorder.visible
const domaineReorderSaving = domaineReorder.saving
const domaineReorderDirty = domaineReorder.isDirty
const domaineReorderAnnouncement = domaineReorder.announcement
const domaineReorderDiscardOpen = domaineReorder.showDiscardConfirm

function onDomaineReorderMove(payload: { groupId: string; keyName: string; fromIndex: number }): void {
  domaineReorder.moveByKeyboard(payload.groupId, payload.keyName, payload.fromIndex)
}

useEditScopeToast(
  isEditing,
  computed(() => (isEditing.value ? directionPageEditScopeNotice(actor.value, directionLib.value) : null)),
)

function sameOrg(left: string | undefined, right: string): boolean {
  return (left ?? '').trim().toLowerCase() === right.trim().toLowerCase()
}

const scopeLimitsManager = computed(() => myManagerOrganigramEditScope.value !== null)

const canAddDomaine = computed(() => {
  if (!scopeLimitsManager.value) return true
  const scope = myManagerOrganigramEditScope.value
  return (scope?.directionLeaders ?? []).some((entry) => sameOrg(entry.dirLib, directionLib.value))
})

function canEditDomaineColumn(domaineLib: string | undefined): boolean {
  if (!scopeLimitsManager.value) return true
  const scope = myManagerOrganigramEditScope.value
  if ((scope?.directionLeaders ?? []).some((entry) => sameOrg(entry.dirLib, directionLib.value))) {
    return true
  }
  return (scope?.domainLeaders ?? []).some(
    (entry) => sameOrg(entry.dirLib, directionLib.value) && sameOrg(entry.domLib, domaineLib ?? '')
  )
}

const emphasizeLimitedScope = computed(() => {
  if (!isEditing.value || actorMayCreateOrDeleteDirection(actor.value) || grid.value.length === 0) {
    return false
  }
  return grid.value.some((column) => !canEditDomaineColumn(column.header?.label))
})

const {
  showOverlayDomaine,
  showOverlayModifyDomaine,
  modifyDomaineContext,
  showOverlayAgence,
  defaultDomaineForAgence,
  showOverlayEquipe,
  showOverlayModifyEquipeDomaine,
  showOverlayModifyEquipeAgence,
  showOverlayModifyAgence,
  modifyEquipeDomaineContext,
  modifyEquipeAgenceContext,
  modifyAgenceContext,
  equipeFormContext,
  goToHrefModifyAgence,
  goToHrefModifyEquipeDomaine,
  goToHrefModifyEquipeAgence,
  domainesForForm,
  domainesWithAgencesForForm,
  onAddClick,
  onModifyClick,
  closeOverlayModifyEquipeDomaine,
  closeOverlayModifyEquipeAgence,
  closeOverlayModifyAgence,
  closeOverlayDomaine,
  closeOverlayModifyDomaine,
  closeOverlayAgence,
  closeOverlayEquipe,
} = useDirectionPageOverlays({ directionLib, grid })

const { setBreadcrumbDomainePreview, clearBreadcrumbDomainePreview } = useDirectionPageBreadcrumbPreview()

/** Un seul `h1` par page : l’encart vide porte le `h1`, le titre direction repasse en `p` le temps de l’affichage. */
const directionPageHeadingTag = computed(() => {
  if (isDirectionOrganisationEmpty.value && !isEditing.value) {
    return 'p' as const
  }
  return 'h1' as const
})

useOrgLocateHighlight({
  isReady: isReadyForHighlight,
  triggers: [
    {
      queryKey: 'highlightDirection',
      resolveSelector: () => {
        const highlightDirection = route.query.highlightDirection as string | undefined
        if (highlightDirection && directionLib.value) {
          return `[data-direction-id="${CSS.escape(highlightDirection)}"]`
        }
        return null
      },
    },
    {
      queryKey: 'highlightDomaine',
      resolveSelector: () => {
        const highlightDomaine = route.query.highlightDomaine as string | undefined
        if (highlightDomaine && grid.value.length > 0) {
          return `[data-domaine-id="${CSS.escape(highlightDomaine)}"]`
        }
        return null
      },
    },
    {
      queryKey: 'highlightAgence',
      resolveSelector: () => {
        const highlightAgence = route.query.highlightAgence as string | undefined
        if (highlightAgence && grid.value.length > 0) {
          return `[data-agence-id="${CSS.escape(highlightAgence)}"]`
        }
        return null
      },
    },
  ],
})
</script>

<template>
  <section class="national" data-onboarding="direction-page" :data-direction-id="directionLib || undefined">
    <DirectionPageLoadingSkeleton v-if="pageState === 'loading'" />

    <AppErrorPageBlock
      v-else-if="(pageState === 'error' || pageState === 'notFound') && errorScreenModel"
      class="national-error-screen"
      :model="errorScreenModel"
    />

    <template v-else>
      <Transition name="fade-slide" mode="out-in">
        <component
          :is="directionPageHeadingTag"
          key="title"
          class="page-title"
          data-onboarding="direction-title"
        >
          {{ title }}
        </component>
      </Transition>

      <DirectionPageCodirBar
        :direction-lib="directionLib"
        :codir="codir"
        :can-mutate="canAddDomaine"
        :in-scope="emphasizeLimitedScope && canAddDomaine"
        @add-click="onAddClick"
        @modify-click="onModifyClick"
      />

      <div ref="domaineReorderRoot">
        <DirectionDomainesGrid
          :grid="grid"
          :direction-lib="directionLib"
          :is-editing="isEditing"
          :is-reordering="domaineReorderVisible"
          :emphasize-limited-scope="emphasizeLimitedScope"
          :can-add-domaine="canAddDomaine"
          :can-edit-domaine-column="canEditDomaineColumn"
          :is-direction-organisation-empty="isDirectionOrganisationEmpty"
          :can-edit-organigram-on-current-view="canEditOrganigramOnCurrentView"
          @add-click="onAddClick"
          @modify-click="onModifyClick"
          @mouseenter-col="setBreadcrumbDomainePreview"
          @mouseleave-col="clearBreadcrumbDomainePreview"
          @reorder-move="onDomaineReorderMove"
        />
      </div>

      <OrgReorderChrome
        :visible="domaineReorderVisible && domaineReorderDirty"
        :saving="domaineReorderSaving"
        :announcement="domaineReorderAnnouncement"
        :show-discard-confirm="domaineReorderDiscardOpen"
        @cancel="domaineReorder.cancelDraft"
        @save="domaineReorder.save"
        @confirm-discard="domaineReorder.confirmDiscard"
        @cancel-discard="domaineReorder.cancelDiscard"
      />

      <DirectionPageOverlays
        :direction-lib="directionLib"
        :show-overlay-domaine="showOverlayDomaine"
        :show-overlay-modify-domaine="showOverlayModifyDomaine"
        :modify-domaine-context="modifyDomaineContext"
        :show-overlay-agence="showOverlayAgence"
        :default-domaine-for-agence="defaultDomaineForAgence"
        :show-overlay-equipe="showOverlayEquipe"
        :show-overlay-modify-equipe-domaine="showOverlayModifyEquipeDomaine"
        :show-overlay-modify-equipe-agence="showOverlayModifyEquipeAgence"
        :show-overlay-modify-agence="showOverlayModifyAgence"
        :modify-equipe-domaine-context="modifyEquipeDomaineContext"
        :modify-equipe-agence-context="modifyEquipeAgenceContext"
        :modify-agence-context="modifyAgenceContext"
        :equipe-form-context="equipeFormContext"
        :go-to-href-modify-agence="goToHrefModifyAgence"
        :go-to-href-modify-equipe-domaine="goToHrefModifyEquipeDomaine"
        :go-to-href-modify-equipe-agence="goToHrefModifyEquipeAgence"
        :domaines-for-form="domainesForForm"
        :domaines-with-agences-for-form="domainesWithAgencesForForm"
        @refetch="refetch"
        @close-overlay-domaine="closeOverlayDomaine"
        @close-overlay-modify-domaine="closeOverlayModifyDomaine"
        @close-overlay-agence="closeOverlayAgence"
        @close-overlay-equipe="closeOverlayEquipe"
        @close-overlay-modify-equipe-domaine="closeOverlayModifyEquipeDomaine"
        @close-overlay-modify-equipe-agence="closeOverlayModifyEquipeAgence"
        @close-overlay-modify-agence="closeOverlayModifyAgence"
      />
    </template>
  </section>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/partials/orga/national-grid" as *;
@use "~/assets/scss/partials/elpdl/spacing" as spacing;
@use "~/assets/scss/partials/orga/orga-page-animations" as motion;

@include motion.fade-slide-transitions;
@include motion.org-locate-highlight;

.national-error-screen {
  margin: spacing.$spacing-xl auto;
}
</style>
